import React, { useMemo, useState } from 'react'
import { Button } from '@components/common'
import { IFaxFolder, IFaxTag } from '@dataTypes/fax.type'
import type { FaxPreviewHooks, FaxSortOrder } from './useFaxPreview'
import { CheckBoxComponent } from '@syncfusion/ej2-react-buttons'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import MoveFaxesModal from './modals/MoveFaxesModal.component'
import MessageSortDropdown, {
  type MessageSortMenuSection,
} from '../../shared/MessageSortDropdown.component'

type Props = {
  hooks: FaxPreviewHooks
  folders: IFaxFolder[]
  tags: IFaxTag[]
  mode?: 'list' | 'detail'
}

const FaxListToolbar: React.FC<Props> = ({
  hooks,
  folders,
  tags,
  mode = 'list',
}) => {
  const { capabilities } = hooks
  const [showMoveModal, setShowMoveModal] = useState(false)
  const selectionFaxes = hooks.selectionFaxes
  const readIcon = selectionFaxes.some((f) => !f.is_read)
    ? 'mgc_mail_open_fill'
    : 'mgc_mail_fill'
  const noSelection = selectionFaxes.length === 0
  const blockDisabled = !hooks.canBlockUnblockSelected
  const isDetailMode = mode === 'detail'

  const faxSortSections: MessageSortMenuSection[] = useMemo(() => {
    const isDateField = hooks.sortField === 'date' || hooks.sortField === null
    return [
      {
        id: 'field',
        label: 'Sort By',
        selectedId: hooks.sortField,
        items: [
          { id: 'date', label: 'Date' },
          { id: 'subject', label: 'Subject' },
          { id: 'number', label: 'From/To' },
        ],
      },
      {
        id: 'order',
        label: 'Sort Direction',
        selectedId: hooks.sortField ? hooks.sortOrder : null,
        items: isDateField
          ? [
              { id: 'desc', label: 'Newest first' },
              { id: 'asc', label: 'Oldest first' },
            ]
          : [
              { id: 'asc', label: 'A to Z' },
              { id: 'desc', label: 'Z to A' },
            ],
      },
    ]
  }, [hooks.sortField, hooks.sortOrder])

  const handleFaxSortSelect = (sectionId: string, itemId: string): void => {
    if (sectionId === 'field') {
      hooks.handleSortFieldChange(itemId as 'date' | 'subject' | 'number')
      return
    }
    if (sectionId === 'order') {
      hooks.handleSortOrderChange(itemId as FaxSortOrder)
    }
  }

  const renderActionIcons = (): React.ReactNode => (
    <>
      {!isDetailMode && capabilities.showRefresh && (
        <>
          <button
            type="button"
            className="unstyled-button fax-toolbar-icon mgc_refresh_1_line"
            title="Refresh"
            aria-label="Refresh"
            onClick={hooks.handleRefresh}
          />
          <div className="vertical-divider" />
        </>
      )}
      {capabilities.showReadToggle && (
        <>
          <button
            type="button"
            className={`unstyled-button fax-toolbar-icon ${readIcon} ${noSelection ? 'disabled' : ''}`}
            title="Mark as read/unread"
            aria-label="Mark as read/unread"
            disabled={noSelection}
            onClick={hooks.handleToggleReadSelected}
          />
          <div className="vertical-divider" />
        </>
      )}
      {capabilities.showDelete && (
        <button
          type="button"
          className={`unstyled-button fax-toolbar-icon mgc_delete_2_fill ${noSelection ? 'disabled' : ''}`}
          title="Delete"
          aria-label="Delete"
          disabled={noSelection}
          onClick={() => {
            if (noSelection) return
            hooks.handleMultipleSelectAction(
              hooks.activeFolder === 'trash' ? 'permanently_delete' : 'delete',
              selectionFaxes as Parameters<
                typeof hooks.handleMultipleSelectAction
              >[1],
            )
          }}
        />
      )}
      {capabilities.showRecover && (
        <button
          type="button"
          className={`unstyled-button fax-toolbar-icon mgc_back_line ${noSelection ? 'disabled' : ''}`}
          title="Restore to inbox"
          aria-label="Restore to inbox"
          disabled={noSelection}
          onClick={() => {
            if (noSelection) return
            hooks.handleRecoverSelected()
          }}
        />
      )}
      {capabilities.showArchiveAction && (
        <button
          type="button"
          className={`unstyled-button fax-toolbar-icon mgc_archive_fill ${noSelection ? 'disabled' : ''}`}
          title={
            capabilities.archiveActionIsUnarchive ? 'Unarchive' : 'Archive'
          }
          aria-label={
            capabilities.archiveActionIsUnarchive ? 'Unarchive' : 'Archive'
          }
          disabled={noSelection}
          onClick={() => {
            if (noSelection) return
            hooks.handleMultipleSelectAction(
              capabilities.archiveActionIsUnarchive ? 'unarchive' : 'archive',
              selectionFaxes as Parameters<
                typeof hooks.handleMultipleSelectAction
              >[1],
            )
          }}
        />
      )}
      <button
        type="button"
        className={`unstyled-button fax-toolbar-icon ${
          hooks.activeFolder === 'blocked'
            ? 'mgc_corner_up_left_line'
            : hooks.isUnblockMode
              ? 'mgc_user_x_fill'
              : 'mgc_user_forbid_fill'
        } ${blockDisabled ? 'disabled' : ''}`}
        title={hooks.isUnblockMode ? 'Unblock' : 'Block'}
        aria-label={hooks.isUnblockMode ? 'Unblock' : 'Block'}
        disabled={blockDisabled}
        onClick={() => {
          if (blockDisabled) return
          hooks.handleBlockUnblockSelected()
        }}
      />
      <div className="vertical-divider" />
      {capabilities.showMoveToFolder && (
        <>
          <button
            type="button"
            className={`unstyled-button fax-toolbar-icon mgc_folder_upload_fill ${noSelection ? 'disabled' : ''}`}
            title="Move to folder"
            aria-label="Move to folder"
            disabled={noSelection}
            onClick={() => {
              if (noSelection) return
              setShowMoveModal(true)
            }}
          />
        </>
      )}
      {capabilities.showApplyLabel && (
        <DropDownListComponent
          enabled={!noSelection}
          className="fax-toolbar-icon e-caret-hide"
          cssClass="fax-tag-selection-dd"
          dataSource={tags.map((tag) => ({
            text: tag.tag_name,
            value: tag.fax_tag_id!,
          }))}
          fields={{ text: 'text', value: 'value' }}
          allowFiltering={true}
          select={(args) => hooks.onSelectTag(args.itemData.value as string)}
        />
      )}
    </>
  )

  return (
    <div className="fax-list-toolbar">
      <div className="fax-list-toolbar-left">
        {isDetailMode ? (
          <button
            type="button"
            className="unstyled-button fax-list-toolbar-back"
            title="Back"
            onClick={hooks.handleBackFromDetail}
          >
            <span className="fax-list-toolbar-back-icon mgc_left_line" />
            Back
          </button>
        ) : (
          <CheckBoxComponent
            label="Select all"
            checked={hooks.selectAllChecked}
            indeterminate={hooks.selectAllIndeterminate}
            change={hooks.handleSelectAll}
          />
        )}
        <div className="fax-list-toolbar-left-icons">{renderActionIcons()}</div>
      </div>
      {!isDetailMode && (
        <div className="fax-list-toolbar-right">
          {capabilities.showSort && (
            <MessageSortDropdown
              sections={faxSortSections}
              onSelect={handleFaxSortSelect}
              footerItems={[{ id: 'clear', label: 'Clear sorting' }]}
              onFooterSelect={() => hooks.handleClearSort()}
            />
          )}
          {capabilities.showToolbarPagination && (
            <div className="fax-pagination">
              <Button
                className="button-icon fax-pagination-left"
                disabled={hooks.currentPage <= 1}
                onClick={() => hooks.handlePageChange('prev')}
              >
                <i className="fax-pagingation-icon mgc_left_line" />
              </Button>
              <span className="fax-pagination-text">
                {hooks.currentPage}/{hooks.totalPages}
              </span>
              <Button
                className="button-icon fax-pagination-right"
                disabled={hooks.currentPage >= hooks.totalPages}
                onClick={() => hooks.handlePageChange('next')}
              >
                <i className="fax-pagingation-icon mgc_right_line" />
              </Button>
            </div>
          )}
        </div>
      )}
      <MoveFaxesModal
        show={showMoveModal}
        onHide={() => setShowMoveModal(false)}
        folders={folders}
        onMove={hooks.handleMoveToFolder}
      />
    </div>
  )
}

export default FaxListToolbar
